import { Item } from "@aitisal/contracts/restaurant/menu";
import { Button } from "@aitisal/ui/retail-storefront/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@aitisal/ui/retail-storefront/carousel";
import { useNavigate } from "@tanstack/react-router";
import { ReactNode } from "react";
import ResponsiveImage from "../ResponsiveImage";
import { getTitle } from "../../helper/helper";
import { useActiveMenu } from "../../hooks/useActiveMenu";
import ProductCard from "./ProductCard";

// Container Component
function Container({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
  );
}

// Main NewArrivals Component
export default function NewArrivals() {
  const navigate = useNavigate();

  const { sections } = useActiveMenu();

  // Find section with "New Arrival" in the name within the active menu only
  const newArrivalSection = sections.find((section) => {
    const sectionName = getTitle(section?.name || "").toLowerCase();
    return (
      sectionName.includes("new arrival") ||
      sectionName.includes("new arrivals")
    );
  });

  const validItems = (newArrivalSection?.items || [])
    .filter((item: Item) => item.isSeparatelySellable)
    .slice(0, 4);

  // Don't render if no section found or no items
  if (!newArrivalSection || validItems.length === 0) {
    return null;
  }

  const isLoading = false;

  if (isLoading) {
    return (
      <div className="w-full bg-white">
        <Container>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-900 border-t-transparent"></div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="w-full bg-white py-8 md:py-12">
      <Container>
        {/* Mobile Layout: Hero on top, cards with HORIZONTAL SCROLL */}
        <div className="flex flex-col lg:hidden gap-4">
          {/* Hero Section - Mobile */}
          <div className="relative w-full aspect-[5/4] rounded-2xl overflow-hidden">
            <ResponsiveImage
              imageObject={newArrivalSection?.image}
              fallbackSrc="https://images.unsplash.com/photo-1556821840-3a63f95609a7?q=80&w=1000&auto=format&fit=crop"
              sizes="100vw"
              alt="New Arrival"
              className="absolute inset-0 h-full w-full"
              style={{ height: "100%" }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h2 className="text-3xl font-bold text-white mb-3">
                New Arrival
              </h2>
              <Button
                variant="ghost"
                onClick={() =>
                  navigate({
                    to: "/c/shop",
                    search: {
                      category: getTitle(newArrivalSection.name),
                      search: undefined,
                    },
                  })
                }
                className="h-auto w-auto rounded-none px-6 py-1.5 bg-white text-gray-900 font-semibold text-xs hover:bg-gray-100 transition-colors border-b-2 border-gray-900"
              >
                SHOP NOW
              </Button>
            </div>
          </div>

          {/* Product Grid - Mobile Horizontal Scroll */}
          <Carousel
            opts={{ align: "start", containScroll: "trimSnaps" }}
            className="w-full"
          >
            <CarouselContent className="ml-0">
              {validItems.map((item) => (
                <CarouselItem key={item.itemId} className="basis-[55%] pl-3">
                  <ProductCard item={item} isNew={true} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Desktop Layout: Section image and 2x2 card grid aligned uniformly */}
        <div className="hidden lg:flex gap-4 items-stretch">
          {/* Left Side - Section Image */}
          <div className="flex-shrink-0 w-[52%]">
            <div className="relative aspect-[5/6] w-full rounded-2xl overflow-hidden">
              <ResponsiveImage
                imageObject={newArrivalSection?.image}
                fallbackSrc="https://images.unsplash.com/photo-1556821840-3a63f95609a7?q=80&w=1000&auto=format&fit=crop"
                sizes="(min-width: 1024px) 52vw, 100vw"
                alt="New Arrival"
                objectFit="contain"
                className="absolute inset-0 h-full w-full"
                style={{ height: "100%" }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-black/10">
                <h2 className="text-2xl xl:text-3xl font-bold text-white mb-3">
                  New Arrival
                </h2>
                <Button
                  variant="ghost"
                  onClick={() =>
                    navigate({
                      to: "/c/shop",
                      search: {
                        category: getTitle(newArrivalSection.name),
                        search: undefined,
                      },
                    })
                  }
                  className="h-auto w-auto rounded-none px-6 py-1.5 bg-white text-gray-900 font-semibold text-xs hover:bg-gray-100 transition-colors border-b-2 border-gray-900"
                >
                  SHOP NOW
                </Button>
              </div>
            </div>
          </div>

          {/* Right Side - 2x2 Card Grid */}
          <div className="flex-1 min-w-0 flex items-stretch">
            <div className="grid grid-cols-2 gap-4 w-full h-full content-stretch">
              {validItems.map((item) => (
                <div key={item.itemId} className="w-full min-w-0 h-full self-stretch">
                  <ProductCard item={item} isNew={true} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
